import createPrivateMedia from '../../utils/wix-private-media';
import wixmp from 'wixmp-sdk';
import getVideoDuration, { CHUNK_SIZE } from '../../utils/get-video-duration';

import events from './events/index';
import toError from './parsers/error';

import triggerProgressWhileTranscoding from './trigger-progress-while-transcoding';


let uploadQueue = [];

if (wixmp) {
  wixmp.Uploader.on('queueChanged', function (queue) {
    uploadQueue = queue;
  });
}


let checkVideoPromise = Promise.resolve();


const loadDuration = ({ file, settings, isFreeUser }) => {
  const { size, headers } = file;
  const videoSource = file.url || file;
  const fileIsUrl = _.isString(videoSource);

  if (!isFreeUser) {
    return Promise.resolve(0);
  }

  return getVideoDuration(videoSource, settings, { chunkSize: CHUNK_SIZE, headers })
    .catch(reason => {
      if (fileIsUrl || size <= CHUNK_SIZE * 2) {
        // if file is url no need to retry, it will fail
        // file is small, we sent it whole already, no need to send it again, it will fail again
        return Promise.reject(reason);
      }

      return getVideoDuration(videoSource, settings, { chunkSize: CHUNK_SIZE * 3, headers });
    });
};

export default function (settings, adapterMethods) {
  const wixPrivateMedia = createPrivateMedia(settings);

  function _triggerUploadProgress(trigger, fileToUpload, data) {
    const { currentFileQueueIdx } = data;

    if (uploadQueue[currentFileQueueIdx] !== fileToUpload) {
      return;
    }

    const { currentFileProgress } = data;
    const { status, total, loaded } = currentFileProgress;

    trigger(events.UPLOADING_PROGRESS.UPDATE, {
      uploadingStatus: status.toUpperCase(),
      fileSize: total,
      uploadedSize: loaded,
    });
  }

  function upload(trigger, source) {
    const uploadData = {
      uploadFn: wixPrivateMedia.item.upload,
      source,
    };

    const fileToUpload = wixmp.Uploader.upload([uploadData])[0];

    wixmp.Uploader.on('fileStatusChanged', _.partial(_triggerUploadProgress, trigger, fileToUpload));

    return fileToUpload.deferred.promise.catch(toError);
  }

  function uploadByUrl(trigger, source) {
    const uploadData = {
      // skip waiting
      uploadFn: (fileSource, options) => wixPrivateMedia.item.uploadByUrl(fileSource, options, true),
      source,
    };

    const fileToUpload = wixmp.Uploader.upload([uploadData])[0];

    return fileToUpload.deferred.promise.catch(toError);
  }

  function uploadVideo(trigger, source, uploadFn = upload) {
    return uploadFn(trigger, source)
      .then(function (fileResponse) {
        triggerProgressWhileTranscoding();
        return fileResponse;
      });
  }

  function canUploadVideo(trigger, file) {
    // duration ms
    const { size, duration } = file;

    const promise = checkVideoPromise
      .then(() => Promise.all([
        adapterMethods.premium.getPremiumInfo(),
        adapterMethods.premium.getUsage(),
      ]))
      .then(function ([premiumInfo, usageInfo]) {
        const { product, itemMilliseconds } = premiumInfo;
        const isFreeUser = Boolean(itemMilliseconds);

        return duration ? Promise.resolve(duration) : loadDuration({ file, settings, isFreeUser })
          .then(function (duration) {
            const exceeded = [];
            const estimatedUsage = usageInfo.estimated;

            const timeLeft = premiumInfo.milliseconds - estimatedUsage.milliseconds;
            const spaceLeft = premiumInfo.bytes - estimatedUsage.bytes;

            if (size > spaceLeft) {
              exceeded.push('total-space');
            }

            if (isFreeUser) {
              if (duration > itemMilliseconds) {
                exceeded.push('item-time');
              }
              if (duration > timeLeft) {
                exceeded.push('total-time');
              }
            } else if (timeLeft <= 0) {
              exceeded.push('total-time');
            }

            if (exceeded.length) {
              return Promise.reject({
                status: 500,
                data: {
                  errorCode: -1810,
                  errorDescription: 'quota exceeded',
                  errorInfo: {
                    key: 'wmp_error.quota_exceeded',
                    isFree: !product,
                    args: exceeded,
                  },
                },
              });
            }

            return {
              size,
              duration,
            };
          });
      });

    checkVideoPromise = promise
      .catch(_.noop);

    return promise;
  }


  return {
    upload,
    uploadByUrl,
    uploadVideo,
    canUploadVideo,
  };
}



// WEBPACK FOOTER //
// ./src/transport/adapters/vod/file.js